/**
 * Lightweight client-side conversion state. Backed by localStorage so it
 * survives sessions but never leaves the browser — no server tracking,
 * no analytics ping, no "creepy" feel.
 *
 * The site uses this to subtly adapt CTA copy ("Begin the Flow" →
 * "Continue Your Blueprint" if the user has started but not submitted).
 * The state is read on render; we don't push updates through React state
 * because the personalization is intentionally low-stakes and shouldn't
 * cause re-renders.
 */

const KEYS = {
  bookPreviewed: "am_viewed_book_preview",
  bookPurchased: "am_purchased_book",
  snapshotCompleted: "am_completed_snapshot",
  snapshotResult: "am_snapshot_result",
  blueprintStarted: "am_started_blueprint",
  blueprintSubmitted: "am_submitted_blueprint",
  consultationOpened: "am_consultation_opened",
};

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, val) => {
  try {
    localStorage.setItem(key, val);
  } catch {
    /* localStorage may be disabled — silently no-op */
  }
};

export const getConversionState = () => ({
  bookPreviewed: safeGet(KEYS.bookPreviewed) === "1",
  bookPurchased: safeGet(KEYS.bookPurchased) === "1",
  snapshotCompleted: safeGet(KEYS.snapshotCompleted) === "1",
  snapshotResult: (() => {
    try {
      return JSON.parse(safeGet(KEYS.snapshotResult) || "null");
    } catch {
      return null;
    }
  })(),
  blueprintStarted: safeGet(KEYS.blueprintStarted) === "1",
  blueprintSubmitted: safeGet(KEYS.blueprintSubmitted) === "1",
  consultationOpened: safeGet(KEYS.consultationOpened) === "1",
});

export const markBookPreviewed = () => safeSet(KEYS.bookPreviewed, "1");
export const markBookPurchased = () => safeSet(KEYS.bookPurchased, "1");
export const markBlueprintStarted = () => safeSet(KEYS.blueprintStarted, "1");
export const markBlueprintSubmitted = () => safeSet(KEYS.blueprintSubmitted, "1");
export const markConsultationOpened = () => safeSet(KEYS.consultationOpened, "1");

/**
 * Returns the most relevant next-step CTA for the visitor's current state.
 * Pure function — no React, no DOM. Use anywhere.
 */
export const getSmartCTA = () => {
  const s = getConversionState();
  if (s.blueprintStarted && !s.blueprintSubmitted) {
    return { label: "Continue Your Personal Action Plan", href: "/blueprint/intake" };
  }
  if (s.bookPurchased && !s.blueprintSubmitted) {
    return { label: "Start Your Personal Action Plan", href: "/?checkout=blueprint#blueprint" };
  }
  if (s.snapshotCompleted) {
    return {
      label: "Turn Your Result Into a Next Step",
      href: "/?checkout=blueprint#blueprint",
    };
  }
  if (s.bookPreviewed) {
    return { label: "Read the Book", href: "/book" };
  }
  return { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" };
};
